import { Helmet } from "react-helmet-async";
import { buildPublicAppUrl } from "@/lib/appConfig";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  url?: string;
  type?: "website" | "article";
  keywords?: string;
  noIndex?: boolean;
}

const DEFAULT_TITLE = "Splitkart: Group Gifting & Split Payments for Shopify";
const DEFAULT_DESCRIPTION =
  "Make group gifting easy with Splitkart. Let customers split high-ticket orders at checkout to boost AOV and reach new shoppers instantly.";
const DEFAULT_IMAGE = buildPublicAppUrl("/og-image.png");
const DEFAULT_IMAGE_ALT = "Splitkart group gifting and split payments";
const PRIVATE_ROUTE_PREFIXES = [
  "/admin",
  "/merchant",
  "/merchant-dashboard",
  "/merchant-login",
  "/login",
  "/reset-password",
  "/customer-login",
  "/shakatah-checkout",
  "/shakatah-split",
  "/split/",
  "/purchases/",
  "/events/",
  "/my-purchases",
  "/my-activity",
  "/accept-invite",
  "/oauth-success",
  "/shopify-redirect",
];

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  url,
  type = "website",
  keywords,
  noIndex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | Splitkart` : DEFAULT_TITLE;
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const fullUrl = buildPublicAppUrl(url || pathname);
  const isPrivateRoute = PRIVATE_ROUTE_PREFIXES.some((prefix) =>
    pathname === prefix || pathname.startsWith(prefix),
  );
  const shouldNoIndex = noIndex || isPrivateRoute;
  const absoluteImage = /^https?:\/\//i.test(image) ? image : buildPublicAppUrl(image);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      {keywords && <meta name="keywords" content={keywords} />}
      {shouldNoIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content="Splitkart" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Additional SEO */}
      <meta name="theme-color" content="#7c3aed" />
      <meta name="application-name" content="Splitkart" />
    </Helmet>
  );
}
